/**
 * Message Store Service
 *
 * Handles persistence of chat messages to Supabase.
 * Fire-and-forget pattern - errors logged but don't block UI.
 */

import { supabase, getAuthUserId, authReady } from '../supabase';
import { logConnectionError } from '../analytics/eventLogger';
import { recoverGuestSession } from '../auth/sessionRecovery';
import { saveChatMessages } from '../storage/userDataStorage';
import { canonicalizeSenderId } from '../../utils/identityUtils';
import { SOURCE_BRANCH } from '../../utils/deployInfo';

// Message types that are data-only (persisted for recovery, not shown in chat UI)
const NON_DISPLAY_TYPES = [
  'contact',
  'upgrade_to_profile',
  'contact_saved_signal',
  'profile_data_response',
  'metadata_updated',
  'profile_exchange',
  // "keep tonight" moments — rendered on the Detail shelf, never in chat.
  // (Unread is gated separately by an allow-list in chatHelpers, so a moment is
  // already excluded there — do not touch that list.)
  'moment',
];

/**
 * Persist a message to Supabase
 * Fire-and-forget - errors logged but don't block UI
 */
// Optional profile_exchange envelope fields that ride the stored message both
// ways (present only when set): isReconnection; trigger = how the exchange
// started (in_app_scan | link_scan | proximity — signup-connect KPI counting).
const PXP_ENVELOPE_FIELDS = ['isReconnection', 'trigger'];

// No connect message carries a GPS position (Mark, 2026-10-17): the other person
// receives the place by NAME — neighbourhood, and the venue once confirmed — never
// coordinates. Delivery is the stored row (postgres_changes), so stripping here, at
// the one serializer, covers both delivery and recovery. The sender's own record keeps
// its own coordinates (venue pick lists, remembered places, the reconnect distance).
const withoutCoordinates = (metadata) => {
  if (!metadata?.locationData) return metadata || null;
  const locationData = { ...metadata.locationData };
  delete locationData.coords;
  if (locationData.venueInfo) {
    locationData.venueInfo = { ...locationData.venueInfo };
    delete locationData.venueInfo.coordinates;
  }
  return { ...metadata, locationData };
};

export async function persistMessage(chatRoomId, message) {
  try {
    // The server stamps the session and refuses a guest sender it doesn't
    // own (migration 036) — never write before sign-in has resolved.
    await authReady;
    const write = () =>
      supabase.from('chat_messages').upsert(
        {
          chat_room_id: chatRoomId,
          message_id: message.id,
          type: message.type,
          sender: message.sender,
          sender_id: message.senderId || null,
          sender_profile_user_id: message.senderProfileUserId || null,
          content: buildContent(message),
          timestamp: message.timestamp,
          auth_user_id: getAuthUserId(),
          source_branch: SOURCE_BRANCH,
        },
        {
          onConflict: 'chat_room_id,message_id',
          // Data messages (deterministic IDs) overwrite: sender updates their own row.
          // Display messages (cross-user persisted) dedup: first write wins.
          ignoreDuplicates: ![
            'contact',
            'profile_data_response',
            'metadata_updated',
            'profile_exchange',
            'system',
          ].includes(message.type),
        }
      );
    let { error } = await write();
    // Refused as signed-out: the stored sign-in may have vanished while this
    // page still holds it — record, put it back, and retry this one write once
    if (
      error &&
      /no_session/.test(error.message || '') &&
      (await recoverGuestSession('no_session_write', { chatRoomId, error: error.message }))
    ) {
      ({ error } = await write());
    }

    if (error) {
      console.error('[messageStore] Persist error:', error);
      logConnectionError('chat_persist', {
        chatRoomId,
        messageId: message.id,
        error: error.message,
      });
      return false;
    }
    return true;
  } catch (err) {
    console.error('[messageStore] Persist exception:', err);
    logConnectionError('chat_persist', {
      chatRoomId,
      messageId: message.id,
      error: err.message,
    });
    return false;
  }
}

/**
 * Load message history for a chat room
 */
export async function loadMessageHistory(chatRoomId) {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('chat_room_id', chatRoomId)
      .order('timestamp', { ascending: true })
      .order('message_id', { ascending: true }); // Tiebreaker for deterministic ordering

    if (error) {
      console.error('[messageStore] Load error:', error);
      logConnectionError('chat_load', { chatRoomId, error: error.message });
      return [];
    }

    // Defensive check - data could be null in edge cases
    if (!data) {
      console.warn('[messageStore] No data returned for chatRoomId:', chatRoomId);
      return [];
    }

    const messages = data
      .filter((row) => !NON_DISPLAY_TYPES.includes(row.type))
      .map((row) => deserializeMessage(row));

    // Sync to localStorage — DB is the complete source of truth for all
    // displayed message types (chat, contact_share, system).
    if (messages.length > 0) {
      saveChatMessages(chatRoomId, messages);
    }

    return messages;
  } catch (err) {
    console.error('[messageStore] Load exception:', err);
    logConnectionError('chat_load', { chatRoomId, error: err.message });
    return [];
  }
}

/**
 * Load the latest display message per chat room in a single query.
 * Returns { [chatRoomId]: deserializedMessage } for list page rendering
 * (sort by latest chat, preview, unread indicator).
 * Full history is still loaded per-connection via loadMessageHistory().
 *
 * THROWS on failure (after logging) so list pages can distinguish "couldn't
 * load" from "genuinely no messages" — a failure returned as {} would render
 * the "No chats yet" empty state to a user who has chats.
 */
export async function loadLatestMessages(chatRoomIds) {
  if (!chatRoomIds || chatRoomIds.length === 0) return {};

  let data;
  try {
    const res = await supabase
      .from('chat_messages')
      .select('*')
      .in('chat_room_id', chatRoomIds)
      .not('type', 'in', `(${NON_DISPLAY_TYPES.join(',')})`)
      .order('timestamp', { ascending: false })
      .limit(10000);

    if (res.error) throw new Error(res.error.message);
    data = res.data;
  } catch (err) {
    console.error('[messageStore] loadLatestMessages failed:', err);
    logConnectionError('chat_load_latest', { error: err.message });
    throw err;
  }

  if (!data) return {};

  // Deduplicate: first occurrence per chat_room_id is the most recent (descending order)
  const latestByRoom = {};
  for (const row of data) {
    if (!latestByRoom[row.chat_room_id]) {
      latestByRoom[row.chat_room_id] = deserializeMessage(row);
    }
  }

  return latestByRoom;
}

/**
 * Load data-only messages for recovery (not for chat display).
 * Returns profile_data_response, metadata_updated, and contact messages
 * so ConnectionStateContext can reconstruct missing profileData/anonData.
 */
export async function loadDataMessages(chatRoomId) {
  return loadMessagesByType(chatRoomId, [
    'profile_data_response',
    'metadata_updated',
    'contact',
    'profile_exchange',
  ]);
}

/**
 * Load this room's moments (the "keep tonight" photos/lines) for the Detail
 * shelf. A hidden type, so it never rides loadMessageHistory. Both sides read
 * every moment in the room (delivery is the shared chat_messages row); the
 * Detail model groups them by night key.
 */
export async function loadMoments(chatRoomId) {
  return loadMessagesByType(chatRoomId, ['moment']);
}

/**
 * Shared loader for hidden/data message types: every row of the given types in
 * a room, oldest-first, deserialized. Backs loadDataMessages (recovery) and
 * loadMoments (the Detail shelf).
 */
async function loadMessagesByType(chatRoomId, types) {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('chat_room_id', chatRoomId)
      .in('type', types)
      .order('timestamp', { ascending: true });

    if (error) {
      console.error('[messageStore] loadMessagesByType error:', error);
      return [];
    }
    if (!data) return [];
    return data.map((row) => deserializeMessage(row));
  } catch (err) {
    console.error('[messageStore] loadMessagesByType exception:', err);
    return [];
  }
}

/**
 * Build content JSONB based on message type
 * (exported for round-trip tests with deserializeMessage)
 */
export function buildContent(message) {
  if (message.type === 'chat') {
    const chatContent = { text: message.content || message.message || message.text };
    // Preserve anonName so Profile can extract it when loading from history
    if (message.anonName) chatContent.anonName = message.anonName;
    // Classified Anon share bubbles carry their classification for linked render
    if (message.shareType) chatContent.shareType = message.shareType;
    if (message.shareValue) chatContent.shareValue = message.shareValue;
    if (message.isInviteCta) chatContent.isInviteCta = true;
    if (message.isFreeform) chatContent.isFreeform = true;
    if (message.canCreateProfile !== undefined)
      chatContent.canCreateProfile = message.canCreateProfile;
    if (message.contentType === 'image') chatContent.contentType = 'image';
    return chatContent;
  }
  if (message.type === 'system') {
    const systemContent = { text: message.content };
    if (message.meetingDate) systemContent.meetingDate = message.meetingDate;
    // Number.isFinite, not truthy — 0 (UTC) is a valid offset
    if (Number.isFinite(message.meetingUtcOffset))
      systemContent.meetingUtcOffset = message.meetingUtcOffset;
    return systemContent;
  }
  if (message.type === 'contact_share' || message.type === 'contact') {
    // Share lines — shares-as-speech (social-track #13): single-channel value
    // line or the "Here is my contact" link line
    if (message.speech) {
      const speechContent = { speech: true };
      if (message.contactLink) speechContent.contactLink = true;
      if (message.shareType) speechContent.shareType = message.shareType;
      if (message.shareValue) speechContent.shareValue = message.shareValue;
      return speechContent;
    }
    if (message.cardStyle) {
      // Card-style: iOS contact card from Profile (includes profileData for offline recovery)
      return {
        cardStyle: true,
        profileName: message.profileName,
        sharedContactTypes: message.sharedContactTypes || [],
        profileData: message.profileData || null,
      };
    }
    // Data-only contact message from Anon (carries anonData for Profile-side recovery)
    if (message.type === 'contact' && message.anonData) {
      return {
        anonData: message.anonData,
        contactType: message.contactType || null,
        senderProfileUserId: message.senderProfileUserId || null,
      };
    }
    // Inline contacts: Anon's contact entries (contact_share display messages)
    const inlineContent = { contacts: message.contacts, greeting: message.greeting || null };
    if (message.anonName) inlineContent.anonName = message.anonName;
    return inlineContent;
  }
  if (message.type === 'profile_data_response') {
    const pdrContent = {
      profileData: message.profileData || null,
      connectionMetadata: withoutCoordinates(message.connectionMetadata),
      senderProfileUserId: message.senderProfileUserId || null,
    };
    // Reconnection handshake envelopes are routing artifacts — the flag lets
    // persistence/recovery layers skip materializing them as peer records
    if (message.isReconnection) pdrContent.isReconnection = true;
    // The host's meeting verdict (meetingDecision) — the scanner adopts it
    if (message.meeting) pdrContent.meeting = message.meeting;
    return pdrContent;
  }
  if (message.type === 'metadata_updated') {
    const muContent = {
      connectionMetadata: withoutCoordinates(message.connectionMetadata),
      senderProfileUserId: message.senderProfileUserId || null,
    };
    // A repeat connect's verdict (E3b-1 step 10) — the guest pins a reconnect
    if (message.meeting) muContent.meeting = message.meeting;
    return muContent;
  }
  if (message.type === 'profile_exchange' && message.profileData) {
    const pxpContent = {
      profileData: message.profileData,
      senderProfileUserId: message.senderProfileUserId || null,
    };
    for (const key of PXP_ENVELOPE_FIELDS) if (message[key]) pxpContent[key] = message[key];
    return pxpContent;
  }
  if (message.type === 'moment') {
    // A moment carries its photo under `photoUrl` — NEVER contentType:'image',
    // which would leak it into PhotosSection, chat previews and the dashboard.
    const momentContent = { momentType: message.momentType, nightKey: message.nightKey };
    if (message.meetingKey) momentContent.meetingKey = message.meetingKey;
    if (message.photoUrl) momentContent.photoUrl = message.photoUrl;
    if (message.text) momentContent.text = message.text;
    return momentContent;
  }
  // For other message types, store the whole message
  return message;
}

/**
 * Deserialize DB row to message object
 * Includes legacy sender normalization as safety net
 */
export function deserializeMessage(row) {
  // Normalize legacy sender values (safety net for old data)
  let sender = row.sender;
  if (sender === 'anon_user') sender = 'anon';
  if (sender === 'profile_user') sender = 'profile';

  // Own historical senderIds read back as the stable profile id — full story
  // and the tier-gate rationale live on canonicalizeSenderId (identityUtils).
  const senderId = canonicalizeSenderId(row.sender_id);

  const base = {
    id: row.message_id,
    type: row.type,
    sender,
    senderId,
    senderProfileUserId: row.sender_profile_user_id,
    timestamp: row.timestamp,
  };

  if (row.type === 'chat') {
    const msg = { ...base, content: row.content?.text };
    if (row.content?.anonName) msg.anonName = row.content.anonName;
    if (row.content?.shareType) msg.shareType = row.content.shareType;
    if (row.content?.shareValue) msg.shareValue = row.content.shareValue;
    if (row.content?.isInviteCta) msg.isInviteCta = true;
    if (row.content?.isFreeform) msg.isFreeform = true;
    if (row.content?.canCreateProfile !== undefined)
      msg.canCreateProfile = row.content.canCreateProfile;
    if (row.content?.contentType === 'image') msg.contentType = 'image';
    return msg;
  }
  if (row.type === 'system') {
    const msg = { ...base, content: row.content?.text };
    if (row.content?.meetingDate) msg.meetingDate = row.content.meetingDate;
    // Number.isFinite, not truthy — 0 (UTC) is a valid offset
    if (Number.isFinite(row.content?.meetingUtcOffset))
      msg.meetingUtcOffset = row.content.meetingUtcOffset;
    return msg;
  }
  if (row.type === 'contact_share' || row.type === 'contact') {
    // Share lines — shares-as-speech (social-track #13)
    if (row.content?.speech) {
      const msg = { ...base, speech: true };
      if (row.content.contactLink) msg.contactLink = true;
      if (row.content.shareType) msg.shareType = row.content.shareType;
      if (row.content.shareValue) msg.shareValue = row.content.shareValue;
      return msg;
    }
    if (row.content?.cardStyle) {
      // Card-style: iOS contact card from Profile (includes profileData for offline recovery)
      return {
        ...base,
        cardStyle: true,
        profileName: row.content.profileName,
        sharedContactTypes: row.content.sharedContactTypes || [],
        profileData: row.content.profileData || null,
      };
    }
    // Data-only contact message from Anon (carries anonData for Profile-side recovery)
    if (row.type === 'contact' && row.content?.anonData) {
      return {
        ...base,
        anonData: row.content.anonData,
        contactType: row.content.contactType || null,
        senderProfileUserId: row.content.senderProfileUserId || null,
      };
    }
    // Inline contacts: Anon's contact entries (contact_share display messages)
    const msg = {
      ...base,
      contacts: row.content?.contacts,
      greeting: row.content?.greeting || null,
    };
    if (row.content?.anonName) msg.anonName = row.content.anonName;
    return msg;
  }
  if (row.type === 'profile_data_response') {
    const msg = {
      ...base,
      profileData: row.content?.profileData || null,
      connectionMetadata: row.content?.connectionMetadata || null,
      senderProfileUserId: row.content?.senderProfileUserId || row.sender_profile_user_id || null,
    };
    if (row.content?.isReconnection) msg.isReconnection = true;
    if (row.content?.meeting) msg.meeting = row.content.meeting;
    return msg;
  }
  if (row.type === 'metadata_updated') {
    const msg = {
      ...base,
      connectionMetadata: row.content?.connectionMetadata || null,
      senderProfileUserId: row.content?.senderProfileUserId || row.sender_profile_user_id || null,
    };
    if (row.content?.meeting) msg.meeting = row.content.meeting;
    return msg;
  }
  if (row.type === 'profile_exchange') {
    const msg = {
      ...base,
      profileData: row.content?.profileData || {},
      senderProfileUserId: row.content?.senderProfileUserId || null,
    };
    for (const key of PXP_ENVELOPE_FIELDS) if (row.content?.[key]) msg[key] = row.content[key];
    return msg;
  }
  if (row.type === 'moment') {
    const msg = {
      ...base,
      momentType: row.content?.momentType || null,
      nightKey: row.content?.nightKey || null,
    };
    if (row.content?.meetingKey) msg.meetingKey = row.content.meetingKey;
    if (row.content?.photoUrl) msg.photoUrl = row.content.photoUrl;
    if (row.content?.text) msg.text = row.content.text;
    return msg;
  }
  return { ...base, ...row.content };
}
